import React, { useState, useMemo } from 'react';
import { ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { alpacaService } from '../../services/AlpacaService';
import type { BacktestResult } from '../../services/AlpacaService';
import { ParamSlider } from '../../components/ParamSlider/ParamSlider';
import { useToast } from '../../context/ToastContext';
import styles from './Beanstalk.module.css';

// ── Pillar Definitions ──

const PILLARS = [
  { key: 'trend', label: 'Trend Alignment', desc: 'EMA cross, VWAP, MACD' },
  { key: 'momentum', label: 'Momentum', desc: 'RSI(14), Stochastic K/D' },
  { key: 'volume', label: 'Volume Confirmation', desc: 'Relative Volume, OBV' },
  { key: 'volatility', label: 'Volatility Regime', desc: 'Bollinger %B, ATR%' },
  { key: 'meanReversion', label: 'Mean Reversion', desc: 'Z-score, BB extremes' },
  { key: 'breakout', label: 'Momentum Breakout', desc: 'N-day range position' },
  { key: 'relativeStrength', label: 'Relative Strength', desc: 'vs SPY benchmark' },
  { key: 'timeOfDay', label: 'Time / Seasonality', desc: 'Day-of-week, month' },
] as const;

type Weights = Record<string, number>;

const PRESETS: Record<string, { label: string; weights: Weights }> = {
  balanced: {
    label: 'Balanced',
    weights: {
      trend: 13,
      momentum: 13,
      volume: 13,
      volatility: 12,
      meanReversion: 12,
      breakout: 12,
      relativeStrength: 13,
      timeOfDay: 12,
    },
  },
  momentum: {
    label: 'Momentum Hunter',
    weights: {
      trend: 15,
      momentum: 30,
      volume: 15,
      volatility: 5,
      meanReversion: 5,
      breakout: 25,
      relativeStrength: 5,
      timeOfDay: 0,
    },
  },
  meanReversion: {
    label: 'Mean Reversion',
    weights: {
      trend: 5,
      momentum: 10,
      volume: 15,
      volatility: 25,
      meanReversion: 30,
      breakout: 5,
      relativeStrength: 10,
      timeOfDay: 0,
    },
  },
  trendFollower: {
    label: 'Trend Follower',
    weights: {
      trend: 30,
      momentum: 15,
      volume: 25,
      volatility: 10,
      meanReversion: 0,
      breakout: 15,
      relativeStrength: 5,
      timeOfDay: 0,
    },
  },
};

const DEFAULT_WEIGHTS: Weights = {
  trend: 20,
  momentum: 15,
  volume: 15,
  volatility: 10,
  meanReversion: 15,
  breakout: 10,
  relativeStrength: 10,
  timeOfDay: 5,
};

// ── Component ──

const Beanstalk: React.FC = () => {
  const { addToast } = useToast();

  // Config state
  const [weights, setWeights] = useState<Weights>({ ...DEFAULT_WEIGHTS });
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [symbol, setSymbol] = useState('SPY');
  const [years, setYears] = useState(3);
  const [entryThreshold, setEntryThreshold] = useState(75);
  const [exitThreshold, setExitThreshold] = useState(40);
  const [stopLossPct, setStopLossPct] = useState(3);
  const [takeProfitPct, setTakeProfitPct] = useState(6);
  const [maxHoldDays, setMaxHoldDays] = useState(15);

  // Result state
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<BacktestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const totalWeight = useMemo(() => Object.values(weights).reduce((s, v) => s + v, 0), [weights]);

  const setWeight = (key: string, val: number) => {
    setWeights((prev) => ({ ...prev, [key]: val }));
    setActivePreset(null);
  };

  const applyPreset = (key: string) => {
    setWeights({ ...PRESETS[key].weights });
    setActivePreset(key);
  };

  const handleRun = async () => {
    if (!symbol.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await alpacaService.runBacktest({
        strategyType: 'sd-score',
        symbol: symbol.trim().toUpperCase(),
        years,
        initialCapital: 100000,
        config: {
          weights,
          entryThreshold,
          exitThreshold,
          stopLossPct,
          takeProfitPct,
          maxHoldDays,
        },
      });
      setResult(data);
      if (data.metrics.totalTrades === 0) {
        addToast('No trades generated \u2014 try lowering the entry threshold', 'info');
      } else {
        addToast(
          `Backtest complete: ${data.metrics.totalTrades} trades, ${data.metrics.totalReturn.toFixed(1)}% return`,
          'success',
        );
      }
    } catch (err: any) {
      setError(err.message || 'Backtest failed');
      addToast(err.message || 'Backtest failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const m = result?.metrics;

  // Merge score series with equity curve for dual-axis chart
  const chartData = useMemo(() => {
    if (!result?.equityCurve) return [];
    const scoreMap = new Map<string, number>();
    for (const s of result.scoreSeries || []) {
      scoreMap.set(s.date, s.score);
    }
    return result.equityCurve.map((e) => ({
      date: e.date,
      equity: e.equity,
      score: scoreMap.get(e.date) ?? null,
    }));
  }, [result]);

  const scoreClass = (score: number) => {
    if (score >= 70) return styles.scoreHigh;
    if (score >= 45) return styles.scoreMid;
    return styles.scoreLow;
  };

  // Formula string
  const formulaStr = useMemo(() => {
    if (totalWeight === 0) return 'SD Score = 50 (no weights set)';
    const parts = PILLARS.filter((p) => weights[p.key] > 0).map(
      (p) => `${p.label.split(' ')[0]}(${Math.round((weights[p.key] / totalWeight) * 100)}%)`,
    );
    return `SD Score = ${parts.join(' + ')}`;
  }, [weights, totalWeight]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>SignalDeck's Beanstalk</h1>
      </div>
      <p className={styles.subtitle}>Proving Grounds \u2014 Build and backtest your proprietary SD Score</p>

      {/* ── Presets ── */}
      <div className={styles.presetRow}>
        {Object.entries(PRESETS).map(([key, preset]) => (
          <button
            key={key}
            className={activePreset === key ? styles.presetActive : styles.presetBtn}
            onClick={() => applyPreset(key)}
          >
            {preset.label}
          </button>
        ))}
      </div>

      {/* ── Weight Panel ── */}
      <div className={styles.section}>
        <p className={styles.sectionTitle}>Pillar Weights</p>
        <div className={styles.weightGrid}>
          {PILLARS.map((p) => (
            <ParamSlider
              key={p.key}
              label={p.label}
              helpText={p.desc}
              value={weights[p.key]}
              min={0}
              max={100}
              step={5}
              onChange={(v) => setWeight(p.key, v)}
            />
          ))}
        </div>
        <div className={styles.totalWeight}>
          Total weight: {totalWeight} \u2014 each pillar contributes{' '}
          {totalWeight > 0 ? `proportionally (normalized)` : 'nothing'}
        </div>
      </div>

      {/* ── Thresholds ── */}
      <div className={styles.section}>
        <p className={styles.sectionTitle}>Entry / Exit Thresholds</p>
        <div className={styles.weightGrid}>
          <ParamSlider
            label="Entry Score"
            helpText="Enter when SD Score above this"
            value={entryThreshold}
            min={50}
            max={95}
            step={5}
            onChange={setEntryThreshold}
          />
          <ParamSlider
            label="Exit Score"
            helpText="Exit when SD Score drops below this"
            value={exitThreshold}
            min={20}
            max={60}
            step={5}
            onChange={setExitThreshold}
          />
          <ParamSlider
            label="Stop Loss %"
            helpText="Max loss before forced exit"
            value={stopLossPct}
            min={1}
            max={10}
            step={0.5}
            onChange={setStopLossPct}
            formatValue={(v) => `${v}%`}
          />
          <ParamSlider
            label="Take Profit %"
            helpText="Take profit target"
            value={takeProfitPct}
            min={2}
            max={20}
            step={1}
            onChange={setTakeProfitPct}
            formatValue={(v) => `${v}%`}
          />
        </div>
      </div>

      {/* ── Config + Run ── */}
      <div className={styles.configRow}>
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Symbol</span>
          <input
            className={styles.input}
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            style={{ minWidth: 100 }}
          />
        </div>
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Lookback</span>
          <select className={styles.select} value={years} onChange={(e) => setYears(Number(e.target.value))}>
            <option value={1}>1 Year</option>
            <option value={2}>2 Years</option>
            <option value={3}>3 Years</option>
            <option value={5}>5 Years</option>
          </select>
        </div>
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Max Hold Days</span>
          <select
            className={styles.select}
            value={maxHoldDays}
            onChange={(e) => setMaxHoldDays(Number(e.target.value))}
          >
            <option value={5}>5 days</option>
            <option value={10}>10 days</option>
            <option value={15}>15 days</option>
            <option value={30}>30 days</option>
          </select>
        </div>
        <button className={styles.runBtn} onClick={handleRun} disabled={loading || !symbol.trim()}>
          {loading ? 'Running...' : 'Run Backtest'}
        </button>
      </div>

      {/* ── Formula ── */}
      <div className={styles.formula}>
        <span className={styles.formulaHighlight}>SD Score</span> = {formulaStr.replace('SD Score = ', '')}
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <p>
            Running SD Score backtest on {symbol} ({years}yr)...
          </p>
        </div>
      )}

      {/* ── Error ── */}
      {error && <div className={styles.errorState}>{error}</div>}

      {/* ── Results ── */}
      {m && m.totalTrades > 0 && !loading && (
        <>
          {/* Metrics */}
          <div className={styles.metricsGrid}>
            <div className={styles.metricCard}>
              <span className={styles.metricLabel}>Total Return</span>
              <span className={`${styles.metricValue} ${m.totalReturn >= 0 ? styles.positive : styles.negative}`}>
                {m.totalReturn >= 0 ? '+' : ''}
                {m.totalReturn.toFixed(1)}%
              </span>
            </div>
            <div className={styles.metricCard}>
              <span className={styles.metricLabel}>Win Rate</span>
              <span className={styles.metricValue}>{m.winRate.toFixed(1)}%</span>
            </div>
            <div className={styles.metricCard}>
              <span className={styles.metricLabel}>Sharpe Ratio</span>
              <span className={`${styles.metricValue} ${m.sharpe >= 1 ? styles.positive : ''}`}>
                {m.sharpe.toFixed(2)}
              </span>
            </div>
            <div className={styles.metricCard}>
              <span className={styles.metricLabel}>Max Drawdown</span>
              <span className={`${styles.metricValue} ${styles.negative}`}>{m.maxDrawdown.toFixed(1)}%</span>
            </div>
            <div className={styles.metricCard}>
              <span className={styles.metricLabel}>Profit Factor</span>
              <span className={styles.metricValue}>{m.profitFactor.toFixed(2)}</span>
            </div>
            <div className={styles.metricCard}>
              <span className={styles.metricLabel}>Trades</span>
              <span className={styles.metricValue}>{m.totalTrades}</span>
            </div>
          </div>

          {/* Dual-Axis Chart: Equity + SD Score */}
          {chartData.length > 0 && (
            <div className={styles.chartWrap}>
              <p className={styles.chartTitle}>Equity Curve + SD Score Over Time</p>
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={chartData}>
                  <defs>
                    <linearGradient id="equityGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#27ae60" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#27ae60" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: '#666' }}
                    tickFormatter={(d: string) => d.slice(5)}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    yAxisId="equity"
                    tick={{ fontSize: 10, fill: '#666' }}
                    tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`}
                  />
                  <YAxis
                    yAxisId="score"
                    orientation="right"
                    domain={[0, 100]}
                    tick={{ fontSize: 10, fill: '#999' }}
                    tickFormatter={(v: number) => `${v}`}
                  />
                  <Tooltip
                    contentStyle={{ background: '#1a1f2e', border: '1px solid #333', borderRadius: 8, fontSize: 12 }}
                    formatter={(value: number, name: string) =>
                      name === 'equity' ? [`$${value.toLocaleString()}`, 'Equity'] : [`${value}`, 'SD Score']
                    }
                    labelFormatter={(label: string) => label}
                  />
                  <ReferenceLine
                    yAxisId="score"
                    y={entryThreshold}
                    stroke="#27ae60"
                    strokeDasharray="5 5"
                    label={{ value: 'Entry', fill: '#27ae60', fontSize: 10 }}
                  />
                  <ReferenceLine
                    yAxisId="score"
                    y={exitThreshold}
                    stroke="#e74c3c"
                    strokeDasharray="5 5"
                    label={{ value: 'Exit', fill: '#e74c3c', fontSize: 10 }}
                  />
                  <Area
                    yAxisId="equity"
                    type="monotone"
                    dataKey="equity"
                    stroke="#27ae60"
                    fill="url(#equityGrad)"
                    strokeWidth={2}
                  />
                  <Line
                    yAxisId="score"
                    type="monotone"
                    dataKey="score"
                    stroke="#3b82f6"
                    strokeWidth={1.5}
                    dot={false}
                    connectNulls
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Trades Table */}
          {result.trades.length > 0 && (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Entry</th>
                    <th>Exit</th>
                    <th>Entry $</th>
                    <th>Exit $</th>
                    <th>SD Score In</th>
                    <th>SD Score Out</th>
                    <th>Hold</th>
                    <th>P&L</th>
                    <th>P&L %</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {result.trades
                    .slice(-50)
                    .reverse()
                    .map((t, i) => (
                      <tr key={i}>
                        <td>{t.entryDate}</td>
                        <td>{t.exitDate}</td>
                        <td>${t.entryPrice.toFixed(2)}</td>
                        <td>${t.exitPrice.toFixed(2)}</td>
                        <td>{t.sdScore != null && <span className={scoreClass(t.sdScore)}>{t.sdScore}</span>}</td>
                        <td>
                          {t.sdScoreExit != null && <span className={scoreClass(t.sdScoreExit)}>{t.sdScoreExit}</span>}
                        </td>
                        <td>{t.holdDays}d</td>
                        <td className={t.pnl >= 0 ? styles.positive : styles.negative}>
                          {t.pnl >= 0 ? '+' : ''}${Math.abs(t.pnl).toFixed(0)}
                        </td>
                        <td className={t.pnlPct >= 0 ? styles.positive : styles.negative}>
                          {t.pnlPct >= 0 ? '+' : ''}
                          {t.pnlPct.toFixed(1)}%
                        </td>
                        <td>{t.exitReason.replace(/_/g, ' ')}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Empty State ── */}
      {!result && !loading && !error && (
        <div className={styles.emptyState}>
          Configure your SD Score recipe above and click <strong>Run Backtest</strong> to see how it performs.
        </div>
      )}
    </div>
  );
};

export default Beanstalk;
